/*
 * Vajihi Labs - Email Signature Builder
 * Templates view: a Basic vs Browse Themes toggle above the theme list.
 * Basic is a single plain layout (logo, name, title, company, plain
 * contact line — no color, no icons) for people who don't want a themed
 * design at all. Browse Themes shows the single-column list of full,
 * uncropped, scaled previews for the 5 premium themes, unchanged from
 * before. Browsing only ever updates a local candidate — the shared
 * model's themeId changes only when "Customize" is pressed. Leaving this
 * view any other way (switching tabs) discards the candidate automatically,
 * since it was never written to the model.
 */

import { getModel, updateModel } from "./lib/appState";
import { BASIC_THEME, isSampleData, renderTheme, THEMES } from "./lib/themes";
import type { ThemeId } from "./lib/themes";
import { renderScaledPreview } from "./lib/scale";

type Mode = "basic" | "themed";

let candidateThemeId: ThemeId | null = null;
let mode: Mode = "basic";

function byId<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function themeName(id: ThemeId): string {
  if (id === "basic") return BASIC_THEME.name;
  return THEMES.find((t) => t.id === id)?.name ?? "";
}

function renderBasicPanel(): void {
  const panel = byId<HTMLElement>("templates-basic-panel");
  const card = panel?.querySelector<HTMLElement>(".theme-card--basic");
  if (!panel || !card) return;
  const model = getModel();

  const wrapper = card.querySelector<HTMLElement>(".preview-wrapper");
  const stage = card.querySelector<HTMLElement>(".preview-stage");
  if (wrapper && stage) {
    const previewModel = { ...model, themeId: "basic" as ThemeId };
    renderScaledPreview(wrapper, stage, renderTheme(previewModel));
  }
}

function renderThemedList(): void {
  const grid = byId<HTMLElement>("templates-list");
  if (!grid) return;
  const model = getModel();
  const sample = isSampleData(model);
  grid.innerHTML = "";

  THEMES.forEach((meta) => {
    const isSelected = meta.id === candidateThemeId;
    const card = document.createElement("button");
    card.type = "button";
    card.className = "theme-card";
    card.setAttribute("aria-pressed", String(isSelected));
    card.setAttribute("aria-label", `${meta.name} — ${meta.caption}${isSelected ? " (selected)" : ""}`);
    card.innerHTML =
      `<div class="theme-card__preview-wrap"><div class="preview-wrapper"><div class="preview-stage"></div></div></div>` +
      (isSelected ? '<span class="theme-card__check" aria-hidden="true">&#10003;</span>' : "") +
      (sample ? '<span class="theme-card__sample-badge">Sample</span>' : "") +
      `<span class="theme-card__label"><span class="theme-card__name">${meta.name}</span> — ` +
      `<span class="theme-card__caption">${meta.caption}</span></span>`;
    card.addEventListener("click", () => {
      candidateThemeId = meta.id;
      renderThemedList();
      updateCommitButton();
    });
    grid.appendChild(card);

    const wrapper = card.querySelector<HTMLElement>(".preview-wrapper");
    const stage = card.querySelector<HTMLElement>(".preview-stage");
    if (wrapper && stage) {
      const previewModel = { ...model, themeId: meta.id };
      renderScaledPreview(wrapper, stage, renderTheme(previewModel));
    }
  });
}

function updateCommitButton(): void {
  const commitBtn = byId<HTMLButtonElement>("templates-commit-btn");
  const activeId = candidateThemeId ?? getModel().themeId;
  if (commitBtn) commitBtn.textContent = `Customize ${themeName(activeId)}`;
}

function applyModeToDom(): void {
  const basicPanel = byId<HTMLElement>("templates-basic-panel");
  const themedList = byId<HTMLElement>("templates-list");
  const count = byId<HTMLElement>("templates-count");
  const basicToggle = byId<HTMLButtonElement>("templates-mode-basic");
  const themedToggle = byId<HTMLButtonElement>("templates-mode-themed");

  const isBasic = mode === "basic";
  if (basicPanel) basicPanel.hidden = !isBasic;
  if (themedList) themedList.hidden = isBasic;
  if (count) count.hidden = isBasic;
  basicToggle?.setAttribute("aria-selected", String(isBasic));
  themedToggle?.setAttribute("aria-selected", String(!isBasic));
}

function setMode(next: Mode): void {
  mode = next;
  if (mode === "basic") {
    candidateThemeId = "basic";
  } else if (!candidateThemeId || candidateThemeId === "basic") {
    candidateThemeId = THEMES[0].id;
  }
  applyModeToDom();
  if (mode === "basic") {
    renderBasicPanel();
  } else {
    renderThemedList();
  }
  updateCommitButton();
}

/** Called every time Templates becomes the active view — copies the
 *  currently committed theme into the candidate, per STATES-AND-BEHAVIOR's
 *  "On Templates entry copy committed theme to candidate." Opens on
 *  whichever half of the toggle matches what's actually committed, so
 *  someone using a premium theme lands back in Browse Themes, not Basic. */
export function activateTemplates(): void {
  const committed = getModel().themeId;
  candidateThemeId = committed;
  setMode(committed === "basic" ? "basic" : "themed");
}

export function initTemplates(onCommit: () => void): void {
  byId<HTMLButtonElement>("templates-mode-basic")?.addEventListener("click", () => setMode("basic"));
  byId<HTMLButtonElement>("templates-mode-themed")?.addEventListener("click", () => setMode("themed"));

  byId<HTMLButtonElement>("templates-commit-btn")?.addEventListener("click", () => {
    if (candidateThemeId) {
      updateModel((m) => {
        m.themeId = candidateThemeId as ThemeId;
      });
    }
    onCommit();
  });
}
